/**
 * PRD-28 — in-app feedback / bug-report modal.
 *
 * Layout: type toggle (bug / feature) + category picker + smart-
 * placeholder title + description + attachments zone + severity
 * hint chip. Submitting POSTs to /api/feedback (multipart) which
 * mediates the Linear GraphQL call so the API key stays server-side.
 *
 * Niceties baked into v1 per FR-UX / FR-Attachments:
 *   - sessionStorage form recovery on accidental close (5-min window).
 *   - Cmd/Ctrl+Enter submits from the description textarea.
 *   - Anti-double-submit lock (button disabled while submitting).
 *   - File picker + drag-and-drop + clipboard paste for screenshots,
 *     PDFs, logs. Up to 5 files, 10 MB each, 25 MB total.
 *   - Context-aware indigo banner ("adjunta el archivo que falló")
 *     when the bug category plausibly involves a file.
 *
 * Closes via X, Escape, outside-click, or successful submit.
 */

import {
  ALLOWED_ATTACHMENT_MIMES,
  ATTACHMENT_HINTED_BUG_CATEGORIES,
  ATTACHMENT_MAX_BYTES_PER_FILE,
  ATTACHMENT_MAX_BYTES_TOTAL,
  ATTACHMENT_MAX_FILES,
  BUG_CATEGORIES,
  FEATURE_CATEGORIES,
  feedbackApi,
} from "@api/feedback"
import type { BugCategory, FeedbackCategory, FeedbackKind, FeedbackPayload } from "@api/feedback"
import { Button } from "@components/ui/Button"
import { Modal } from "@components/ui/Modal"
import { Select } from "@components/ui/Select"
import { useToast } from "@components/ui/Toaster"
import { analytics } from "@lib/analytics"
import { cn } from "@lib/cn"
import { logger } from "@lib/logger"
import { useTheme } from "@lib/theme"
import {
  Bug,
  FileText,
  Image as ImageIcon,
  Info,
  Lightbulb,
  Paperclip,
  Upload,
  X,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { env } from "@/env"
import i18n from "@/i18n"
import { apiErrorCode } from "@/lib/apiError"

interface Props {
  onClose: () => void
}

interface FormState {
  kind: FeedbackKind
  category: FeedbackCategory | ""
  title: string
  description: string
}

interface AttachmentItem {
  id: string // local-only, for React keys + removal
  file: File
  previewUrl: string | null // object URL for images, null otherwise
  errorKey: string | null // i18n key when client-side validation rejected it
}

const RECOVERY_KEY = "platonica.feedback.draft"
const RECOVERY_TTL_MS = 5 * 60 * 1000 // 5 minutes

const TITLE_MIN = 5
const TITLE_MAX = 120
const DESCRIPTION_MIN = 10
const DESCRIPTION_MAX = 4000

const APP_VERSION = env.VITE_APP_VERSION
const categoryTriggerClass =
  "inline-flex w-full items-center gap-x-2 rounded-control border border-border bg-surface px-3 py-2 text-base text-ink focus:ring-2 md:text-sm focus:outline-none"

const initialState = (): FormState => ({
  kind: "bug",
  category: "",
  title: "",
  description: "",
})

const readDraft = (): FormState | null => {
  try {
    const raw = sessionStorage.getItem(RECOVERY_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state: FormState; savedAt: number }
    if (Date.now() - parsed.savedAt > RECOVERY_TTL_MS) {
      sessionStorage.removeItem(RECOVERY_KEY)
      return null
    }
    return parsed.state
  } catch {
    return null
  }
}

const writeDraft = (state: FormState) => {
  try {
    sessionStorage.setItem(RECOVERY_KEY, JSON.stringify({ state, savedAt: Date.now() }))
  } catch {
    // Quota / private mode — fine, recovery is best-effort.
  }
}

const clearDraft = () => {
  try {
    sessionStorage.removeItem(RECOVERY_KEY)
  } catch {
    // ignore
  }
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const newAttachmentId = (() => {
  let counter = 0
  return () => `att-${Date.now()}-${counter++}`
})()

export function FeedbackModal({ onClose }: Props) {
  const { t } = useTranslation("feedback")
  const toast = useToast()
  const theme = useTheme()
  const [form, setForm] = useState<FormState>(() => readDraft() ?? initialState())
  const [attachments, setAttachments] = useState<AttachmentItem[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const titleRef = useRef<HTMLInputElement | null>(null)
  const descRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // PRD-28 §FR-30 — one-shot mount log.
  useEffect(() => {
    logger.info("feedback.opened", { source: "sidebar" })
  }, [])

  // Persist draft on every change for sessionStorage recovery.
  // Attachments aren't part of the draft — File objects don't survive
  // a tab close anyway, and re-picking is the expected pattern.
  useEffect(() => {
    if (form.title || form.description || form.category) {
      writeDraft(form)
    }
  }, [form])

  // Reset category when kind flips — the option lists don't overlap.
  useEffect(() => {
    setForm((s) => (s.category === "" ? s : { ...s, category: "" }))
  }, [form.kind])

  // No focus effect: the Modal primitive focuses the `data-autofocus` kind
  // card. Focusing the title scrolled the form past its first question.

  // Object-URL cleanup when attachments change / on unmount.
  useEffect(
    () => () => {
      for (const a of attachments) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // ── Attachment helpers ──────────────────────────────────────────
  const validateFile = useCallback((file: File, existing: AttachmentItem[]): string | null => {
    const mime = (file.type || "").toLowerCase()
    if (!ALLOWED_ATTACHMENT_MIMES.has(mime)) {
      return "attachments.error.invalid_mime"
    }
    if (file.size > ATTACHMENT_MAX_BYTES_PER_FILE) {
      return "attachments.error.too_large"
    }
    const total = existing.reduce((sum, a) => sum + a.file.size, 0) + file.size
    if (total > ATTACHMENT_MAX_BYTES_TOTAL) {
      return "attachments.error.too_large_total"
    }
    return null
  }, [])

  const addFiles = useCallback(
    (newFiles: File[]) => {
      setAttachments((prev) => {
        const acc = [...prev]
        for (const file of newFiles) {
          if (acc.length >= ATTACHMENT_MAX_FILES) {
            // Surface a toast for "too many" since the file would be
            // dropped silently otherwise.
            toast.error(t("attachments.error.too_many"))
            break
          }
          const errorKey = validateFile(file, acc)
          const previewUrl =
            (file.type || "").startsWith("image/") && !errorKey ? URL.createObjectURL(file) : null
          acc.push({
            id: newAttachmentId(),
            file,
            previewUrl,
            errorKey,
          })
          if (errorKey) {
            toast.error(t(errorKey))
          }
        }
        return acc
      })
    },
    [t, toast, validateFile],
  )

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id)
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((a) => a.id !== id)
    })
  }, [])

  // ── Clipboard paste handler — PRD-28 §FR-Attachments §9i ────────
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return
      const pasted: File[] = []
      for (const item of items) {
        if (item.kind === "file") {
          const f = item.getAsFile()
          if (f) {
            // Re-name pasted images so they're identifiable in Linear.
            // Clipboard images often arrive as "image.png" — disambiguate
            // with an ISO timestamp.
            const extension = f.type.startsWith("image/jpeg")
              ? "jpg"
              : f.type.startsWith("image/png")
                ? "png"
                : f.type.startsWith("image/webp")
                  ? "webp"
                  : f.type.startsWith("image/gif")
                    ? "gif"
                    : "bin"
            const name = `pasted-${new Date().toISOString().replace(/[:.]/g, "-")}.${extension}`
            pasted.push(new File([f], name, { type: f.type, lastModified: f.lastModified }))
          }
        }
      }
      if (pasted.length > 0) {
        e.preventDefault()
        addFiles(pasted)
        toast.success(t("attachments.toast.pasted", { count: pasted.length }))
      }
    }
    document.addEventListener("paste", onPaste)
    return () => document.removeEventListener("paste", onPaste)
  }, [addFiles, t, toast])

  const categories = form.kind === "bug" ? BUG_CATEGORIES : FEATURE_CATEGORIES
  const titleClean = form.title.trim()
  const descClean = form.description.trim()
  const hasAttachmentError = attachments.some((a) => a.errorKey !== null)
  const canSubmit =
    form.category !== "" &&
    titleClean.length >= TITLE_MIN &&
    titleClean.length <= TITLE_MAX &&
    descClean.length >= DESCRIPTION_MIN &&
    descClean.length <= DESCRIPTION_MAX &&
    !hasAttachmentError

  const showAttachmentHint =
    form.kind === "bug" &&
    form.category !== "" &&
    ATTACHMENT_HINTED_BUG_CATEGORIES.has(form.category as BugCategory)

  const isDirty = useMemo(
    () => !!(form.title || form.description || form.category || attachments.length > 0),
    [form, attachments],
  )
  const categoryOptions = categories.map((categoryOption) => ({
    value: categoryOption as FeedbackCategory | "",
    label: t(`category.${form.kind}.${categoryOption}` as const),
  }))

  const requestClose = () => {
    if (isDirty && !confirmDiscard) {
      setConfirmDiscard(true)
      return
    }
    clearDraft()
    for (const a of attachments) {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
    }
    onClose()
  }

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return
    setSubmitting(true)
    const payload: FeedbackPayload = {
      kind: form.kind,
      category: form.category as FeedbackCategory,
      title: titleClean,
      description: descClean,
      current_url: `${window.location.pathname}${window.location.search}`,
      referrer: document.referrer || "",
      viewport: { width: window.innerWidth, height: window.innerHeight },
      locale: i18n.language || "es-MX",
      theme,
      app_version: APP_VERSION,
      client_submitted_at: new Date().toISOString(),
    }

    try {
      const validFiles = attachments.filter((a) => a.errorKey === null).map((a) => a.file)
      const res = await feedbackApi.submit(payload, validFiles)
      // PRD-28 update (2026-05-22) — show a friendly thank-you toast
      // instead of opening the Linear ticket in a new tab. The ticket
      // URL is for internal triage; professors should never see the
      // Linear UI from a submission.
      toast.success(t("toast.success"))
      analytics.track("feedback_submitted", {
        attachment_count: validFiles.length,
        category: payload.category,
        kind: payload.kind,
      })
      logger.info("feedback.submitted", {
        kind: payload.kind,
        category: payload.category,
        ticket_id: res.ticket_id,
        attachment_count: validFiles.length,
      })
      clearDraft()
      for (const att of attachments) {
        if (att.previewUrl) URL.revokeObjectURL(att.previewUrl)
      }
      onClose()
    } catch (err) {
      const code = apiErrorCode(err)
      toast.error(code ? t(`errors:${code}`, t("toast.error_generic")) : t("toast.error_generic"))
      logger.error("feedback.submit.failed", {
        code,
        kind: payload.kind,
        category: payload.category,
      })
    } finally {
      setSubmitting(false)
    }
  }

  // Cmd/Ctrl+Enter submit from anywhere inside the form.
  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canSubmit) {
      e.preventDefault()
      handleSubmit()
    }
  }

  // ── Drag-and-drop handlers ─────────────────────────────────────
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
  }
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    const dropped = Array.from(e.dataTransfer.files || [])
    if (dropped.length > 0) addFiles(dropped)
  }

  const handlePickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || [])
    if (picked.length > 0) addFiles(picked)
    // Reset the input so picking the same file again still fires onChange.
    e.target.value = ""
  }

  // On the shared Modal: it owns the focus trap, Escape, the scrim, scroll
  // lock and returning focus to "Comentarios". The hand-rolled shell let Tab
  // escape to the page behind after three stops (PRD-59 Etapa B).
  return (
    <Modal
      open
      onClose={requestClose}
      title={t("modal.title")}
      size="lg"
      closeLabel={t("action.cancel")}
      blockScrimClose={submitting}
      footer={
        <div className="space-y-3">
          {confirmDiscard ? (
            <div
              role="alert"
              className="rounded-control bg-attention-soft text-attention-ink p-3 text-sm"
            >
              <p className="font-medium">{t("discard.title")}</p>
              <p className="mt-1">{t("discard.body")}</p>
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    clearDraft()
                    for (const a of attachments) {
                      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
                    }
                    onClose()
                  }}
                >
                  {t("discard.confirm")}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDiscard(false)}>
                  {t("discard.keep")}
                </Button>
              </div>
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={requestClose} disabled={submitting}>
              {t("action.cancel")}
            </Button>
            <Button onClick={handleSubmit} loading={submitting} disabled={!canSubmit || submitting}>
              {t("action.submit")}
            </Button>
          </div>
        </div>
      }
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
        onKeyDown={handleFormKeyDown}
        className="space-y-4"
      >
        {/* ── Type toggle ─────────────────────────────────────── */}
        <fieldset>
          <legend className="text-ink-muted mb-2 block text-sm font-medium">
            {t("field.kind.label")}
          </legend>
          <div className="grid grid-cols-2 gap-2" role="radiogroup">
            <KindCard
              icon={Bug}
              title={t("field.kind.bug")}
              subtitle={t("field.kind.bug_subtitle")}
              active={form.kind === "bug"}
              focusOnOpen={form.kind === "bug"}
              onClick={() => setForm((s) => ({ ...s, kind: "bug" }))}
            />
            <KindCard
              icon={Lightbulb}
              title={t("field.kind.feature")}
              subtitle={t("field.kind.feature_subtitle")}
              active={form.kind === "feature"}
              focusOnOpen={form.kind === "feature"}
              onClick={() => setForm((s) => ({ ...s, kind: "feature" }))}
            />
          </div>
        </fieldset>

        {/* ── Category ────────────────────────────────────────── */}
        <Field label={t("field.category.label")} required>
          <Select
            value={form.category}
            options={categoryOptions}
            placeholder={t("field.category.placeholder")}
            onChange={(value) => setForm((s) => ({ ...s, category: value as FeedbackCategory }))}
            triggerClassName={categoryTriggerClass}
          />
        </Field>

        {/* ── Title (smart placeholder per category) ─────────── */}
        <Field label={t("field.title.label")} required>
          <input
            ref={titleRef}
            required
            minLength={TITLE_MIN}
            maxLength={TITLE_MAX}
            value={form.title}
            onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
            placeholder={
              form.category
                ? t(`placeholder.title.${form.category}` as const, {
                    defaultValue: t("placeholder.title.default"),
                  })
                : t("placeholder.title.default")
            }
            className="rounded-control border-border bg-surface w-full border px-3 py-2 text-base md:text-sm"
          />
          <div className="text-ink-subtle mt-1 flex items-center justify-between text-xs">
            <span>{t("field.title.helper")}</span>
            {/* Not a live region: it announced on every keystroke. */}
            <span>
              {titleClean.length}/{TITLE_MAX}
            </span>
          </div>
        </Field>

        {/* ── Description ─────────────────────────────────────── */}
        <Field label={t("field.description.label")} required>
          <textarea
            ref={descRef}
            required
            minLength={DESCRIPTION_MIN}
            maxLength={DESCRIPTION_MAX}
            rows={5}
            value={form.description}
            onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
            placeholder={t("placeholder.description")}
            className="rounded-control border-border bg-surface w-full border px-3 py-2 text-base md:text-sm"
          />
          <div className="text-ink-subtle mt-1 flex items-center justify-between text-xs">
            <span>{t("field.description.helper")}</span>
            <span>
              {descClean.length}/{DESCRIPTION_MAX}
            </span>
          </div>
        </Field>

        {/* ── Context-aware "attach the failing file" banner ──── */}
        {showAttachmentHint ? (
          <div
            role="note"
            className="rounded-control border-accent bg-accent-soft text-accent-ink flex items-start gap-2 border p-3 text-xs"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{t("attachments.context_hint")}</span>
          </div>
        ) : null}

        {/* ── Attachments zone ────────────────────────────────── */}
        <div>
          <span className="text-ink-muted mb-2 flex items-center gap-1.5 text-sm font-medium">
            <Paperclip className="h-4 w-4" />
            {t("attachments.label")}
          </span>

          <div
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              "rounded-card border-2 border-dashed p-4 transition-colors",
              dragActive ? "border-accent bg-accent-soft" : "border-border bg-inset",
            )}
          >
            <div className="flex flex-col items-center gap-2 text-center">
              <Upload className="text-ink-subtle h-6 w-6" aria-hidden />
              <p className="text-ink-muted text-xs">{t("attachments.drop_hint")}</p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                leftIcon={<Paperclip className="h-3.5 w-3.5" />}
              >
                {t("attachments.pick_cta")}
              </Button>
              <p className="text-ink-subtle text-xs">{t("attachments.caps_hint")}</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp,image/gif,image/heic,application/pdf,text/plain,text/csv,application/json"
              onChange={handlePickFiles}
              className="hidden"
            />
          </div>

          {attachments.length > 0 ? (
            <ul className="mt-2 space-y-1.5">
              {attachments.map((att) => (
                <AttachmentRow
                  key={att.id}
                  attachment={att}
                  onRemove={() => removeAttachment(att.id)}
                />
              ))}
            </ul>
          ) : null}
        </div>
      </form>
    </Modal>
  )
}

// ──────────────────────────────────────────────────────────
// Subcomponents
// ──────────────────────────────────────────────────────────

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="text-ink-muted mb-1 block text-sm font-medium">
        {label}
        {required ? <span className="text-danger ml-1">*</span> : null}
      </span>
      {children}
    </label>
  )
}

function KindCard({
  icon: Icon,
  title,
  subtitle,
  active,
  focusOnOpen,
  onClick,
}: {
  icon: typeof Bug
  title: string
  subtitle: string
  active: boolean
  focusOnOpen?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      data-autofocus={focusOnOpen || undefined}
      onClick={onClick}
      className={cn(
        "group rounded-control flex items-start gap-3 border p-3 text-left transition focus:outline-none focus-visible:ring-2",
        active
          ? "border-accent bg-accent-soft"
          : "border-border bg-surface hover:border-border-strong hover:bg-inset",
      )}
    >
      <span
        className={cn(
          "rounded-control flex h-9 w-9 shrink-0 items-center justify-center",
          active ? "bg-accent-soft text-accent-ink" : "bg-inset text-ink-muted",
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="flex-1">
        <span
          className={cn("block text-sm font-semibold", active ? "text-accent-ink" : "text-ink")}
        >
          {title}
        </span>
        <span className="text-ink-subtle block text-xs">{subtitle}</span>
      </span>
    </button>
  )
}

function AttachmentRow({
  attachment,
  onRemove,
}: {
  attachment: AttachmentItem
  onRemove: () => void
}) {
  const { t } = useTranslation("feedback")
  const isImage = (attachment.file.type || "").startsWith("image/")
  const hasError = attachment.errorKey !== null

  return (
    <li
      className={cn(
        "rounded-control flex items-center gap-2 border p-2",
        hasError ? "border-danger bg-danger-soft" : "border-border bg-surface",
      )}
    >
      <div className="bg-inset text-ink-subtle flex h-10 w-10 shrink-0 items-center justify-center rounded">
        {attachment.previewUrl ? (
          <img
            src={attachment.previewUrl}
            alt={attachment.file.name}
            className="h-full w-full rounded object-cover"
          />
        ) : isImage ? (
          <ImageIcon className="h-4 w-4" />
        ) : (
          <FileText className="h-4 w-4" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "truncate text-xs font-medium",
            hasError ? "text-danger-ink" : "text-ink-muted",
          )}
        >
          {attachment.file.name}
        </p>
        <p className={cn("text-xs", hasError ? "text-danger-ink" : "text-ink-subtle")}>
          {hasError ? t(attachment.errorKey!) : formatBytes(attachment.file.size)}
        </p>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t("attachments.remove")}
        className="rounded-control text-ink-subtle hover:bg-inset hover:text-ink-muted flex h-11 w-11 shrink-0 items-center justify-center md:h-8 md:w-8"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </li>
  )
}
